"use client";

import * as React from "react";
import { ChevronDown } from "@/components/icons";

import { cn } from "@/lib/utils";
import {
  GroupedList,
  GroupedListRow,
  GroupedListSection,
} from "@/components/ui/grouped-list";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

export interface SectionSwitcherItem {
  key: string;
  label: React.ReactNode;
  href?: string;
  onSelect?: () => void;
  icon?: React.ComponentType<{ className?: string }>;
  trailing?: React.ReactNode;
}

interface SectionSwitcherGroup {
  label?: string;
  items: SectionSwitcherItem[];
}

const SectionSwitcherCloseContext = React.createContext<() => void>(() => {});

export function useSectionSwitcherClose() {
  return React.useContext(SectionSwitcherCloseContext);
}

export function SectionSwitcherSheet({
  open,
  onOpenChange,
  title,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: React.ReactNode;
}) {
  const close = React.useCallback(() => onOpenChange(false), [onOpenChange]);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        layout="grouped"
        showCloseButton={false}
        className="md:hidden"
        aria-describedby={undefined}
      >
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>
        <SheetBody>
          <SectionSwitcherCloseContext.Provider value={close}>
            {children}
          </SectionSwitcherCloseContext.Provider>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}

function SectionSwitcherGroups({
  groups,
  activeKey,
}: {
  groups: SectionSwitcherGroup[];
  activeKey: string | null;
}) {
  const close = useSectionSwitcherClose();
  return (
    <GroupedList>
      {groups.map((group, index) => (
        <GroupedListSection key={group.label ?? index} title={group.label}>
          {group.items.map((item) => (
            <GroupedListRow
              key={item.key}
              href={item.href}
              icon={item.icon}
              trailing={item.trailing}
              checked={item.key === activeKey}
              current={item.key === activeKey}
              onSelect={() => {
                item.onSelect?.();
                close();
              }}
            >
              {item.label}
            </GroupedListRow>
          ))}
        </GroupedListSection>
      ))}
    </GroupedList>
  );
}

export function SectionSwitcherTrigger({
  label,
  sheetTitle,
  className,
  onClick,
}: {
  label: React.ReactNode;
  sheetTitle: string;
  className?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-haspopup="dialog"
      className={cn(
        "inline-flex min-h-11 min-w-0 max-w-full items-center gap-1.5 text-left text-base font-semibold text-foreground",
        className,
      )}
    >
      <span
        data-fallback={sheetTitle}
        className="min-w-0 truncate empty:before:content-[attr(data-fallback)]"
      >
        {label}
      </span>
      <span className="sr-only">{`, ${sheetTitle}`}</span>
      <ChevronDown
        className="size-4 shrink-0 text-muted-foreground"
        aria-hidden="true"
      />
    </button>
  );
}

export function SectionSwitcher({
  sheetTitle,
  groups,
  activeKey,
  label,
  className,
}: {
  sheetTitle: string;
  groups: SectionSwitcherGroup[];
  activeKey: string | null;
  label?: React.ReactNode;
  className?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const active = groups
    .flatMap((group) => group.items)
    .find((item) => item.key === activeKey);
  return (
    <div className={cn("md:hidden", className)}>
      <SectionSwitcherTrigger
        label={label ?? active?.label ?? sheetTitle}
        sheetTitle={sheetTitle}
        onClick={() => setOpen(true)}
      />
      <SectionSwitcherSheet
        open={open}
        onOpenChange={setOpen}
        title={sheetTitle}
      >
        <SectionSwitcherGroups groups={groups} activeKey={activeKey} />
      </SectionSwitcherSheet>
    </div>
  );
}
